import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { ArrowLeft, Plus, Clock, Building2, CheckCircle2, XCircle, IndianRupee, Users, MapPin, Calendar } from 'lucide-react';

export default function ClientRFPDashboard() {
  const { user } = useAuth();
  const [rfps, setRFPs] = useState([]);
  const [selectedRFP, setSelectedRFP] = useState(null);
  const [pitches, setPitches] = useState([]);
  const [showPitchesDialog, setShowPitchesDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchRFPs();
  }, []);

  const fetchRFPs = async () => {
    try {
      const res = await api.getBulkRFPs();
      setRFPs(res.data);
    } catch (error) {
      console.error('Failed to fetch RFPs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewPitches = async (rfp) => {
    setSelectedRFP(rfp);
    try {
      const res = await api.getRFPPitches(rfp.id);
      setPitches(res.data);
      setShowPitchesDialog(true);
    } catch (error) {
      toast.error('Failed to fetch pitches');
    }
  };

  const handleUpdatePitchStatus = async (pitchId, status) => {
    try {
      await api.updatePitchStatus(pitchId, status);
      toast.success(`Pitch ${status}`);
      // Refresh pitches
      const res = await api.getRFPPitches(selectedRFP.id);
      setPitches(res.data);
      fetchRFPs();
    } catch (error) {
      toast.error('Failed to update pitch status');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      open: 'bg-emerald-100 text-emerald-800',
      bidding: 'bg-amber-100 text-amber-800',
      closed: 'bg-slate-100 text-slate-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return badges[status] || 'bg-slate-100 text-slate-800';
  };

  const getTimeRemaining = (deadline) => {
    const now = new Date();
    const end = new Date(deadline);
    const diff = end - now;
    
    if (diff <= 0) return 'Expired';
    
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `${minutes}m remaining`;
    
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m remaining`;
  };

  const openRFPs = rfps.filter(r => r.status === 'open');
  const closedRFPs = rfps.filter(r => r.status !== 'open');

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-4">Please Login</h2>
          <Link to="/"><Button>Go to Login</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <nav className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard/client">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
            </Link>
            <h1 className="luxury-text text-xl font-semibold text-midnight-navy">My Bulk RFPs</h1>
          </div>
          <Link to="/bulk-rfp/new">
            <Button className="bg-champagne-gold text-midnight-navy hover:bg-champagne-gold/90">
              <Plus className="w-4 h-4 mr-2" /> New Bulk RFP
            </Button>
          </Link>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-midnight-navy"></div>
          </div>
        ) : rfps.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg border">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-slate-800" />
            <h2 className="text-2xl font-semibold mb-2">No RFPs Yet</h2>
            <p className="text-slate-800 mb-6">Create your first bulk booking request to get quotes from hotels</p>
            <Link to="/bulk-rfp/new">
              <Button className="bg-midnight-navy">
                <Plus className="w-4 h-4 mr-2" /> Create Bulk RFP
              </Button>
            </Link>
          </div>
        ) : (
          <Tabs defaultValue="active">
            <TabsList>
              <TabsTrigger value="active">Active RFPs ({openRFPs.length})</TabsTrigger>
              <TabsTrigger value="closed">Closed ({closedRFPs.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="active" className="mt-6">
              {openRFPs.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border">
                  <p className="text-slate-800">No active RFPs</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {openRFPs.map((rfp) => (
                    <div key={rfp.id} className="bg-white rounded-lg border p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-semibold mb-1">{rfp.event_name}</h3>
                          <p className="text-sm text-slate-800 capitalize">{rfp.event_type.replace('_', ' ')} • {rfp.company_name}</p>
                        </div>
                        <div className="text-right">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(rfp.status)}`}>
                            {rfp.status.toUpperCase()}
                          </span>
                          <div className="mt-2 flex items-center gap-1 text-amber-600">
                            <Clock className="w-4 h-4" />
                            <span className="text-sm font-medium">{getTimeRemaining(rfp.pitch_deadline)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-slate-800" />
                          <span className="text-sm">{rfp.min_attendees}-{rfp.max_attendees} guests</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-slate-800" />
                          <span className="text-sm">{rfp.preferred_cities?.slice(0, 2).join(', ')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-800" />
                          <span className="text-sm">{rfp.event_duration_days} days</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-800" />
                          <span className="text-sm">{rfp.total_rooms_required} rooms</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <IndianRupee className="w-4 h-4 text-slate-800" />
                          <span className="text-sm">₹{(rfp.total_budget_min / 100000).toFixed(1)}L - {(rfp.total_budget_max / 100000).toFixed(1)}L</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-800">Pitches Received:</span>
                          <span className="text-lg font-semibold text-midnight-navy">{rfp.pitches_received}</span>
                          {rfp.is_verified && (
                            <span className="ml-2 px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs rounded-full">
                              ✓ Verified
                            </span>
                          )}
                        </div>
                        <Button onClick={() => handleViewPitches(rfp)} disabled={rfp.pitches_received === 0}>
                          View Pitches
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="closed" className="mt-6">
              {closedRFPs.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border">
                  <p className="text-slate-800">No closed RFPs</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {closedRFPs.map((rfp) => (
                    <div key={rfp.id} className="bg-white rounded-lg border p-6 opacity-75">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-lg font-semibold mb-1">{rfp.event_name}</h3>
                          <p className="text-sm text-slate-800">{rfp.company_name} • {rfp.pitches_received} pitches</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(rfp.status)}`}>
                          {rfp.status.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* Pitches Dialog */}
      <Dialog open={showPitchesDialog} onOpenChange={setShowPitchesDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pitches for {selectedRFP?.event_name}</DialogTitle>
          </DialogHeader>
          
          {pitches.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-slate-800">No pitches received yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pitches.map((pitch) => (
                <div key={pitch.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-lg">{pitch.property_name}</h3>
                      <p className="text-sm text-slate-800">{pitch.vendor_name} • {pitch.property_city}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-midnight-navy">
                        ₹{pitch.total_quoted_amount.toLocaleString()}
                      </p>
                      <p className="text-xs text-slate-800">Total Quote</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-4 mb-4 text-sm">
                    <div className="bg-slate-50 p-3 rounded">
                      <p className="text-slate-800">Accommodation</p>
                      <p className="font-medium">₹{pitch.accommodation_total.toLocaleString()}</p>
                    </div>
                    <div className="bg-slate-50 p-3 rounded">
                      <p className="text-slate-800">Venue</p>
                      <p className="font-medium">₹{pitch.venue_total.toLocaleString()}</p>
                    </div>
                    <div className="bg-slate-50 p-3 rounded">
                      <p className="text-slate-800">F&B</p>
                      <p className="font-medium">₹{pitch.fnb_total.toLocaleString()}</p>
                    </div>
                    <div className="bg-slate-50 p-3 rounded">
                      <p className="text-slate-800">Services</p>
                      <p className="font-medium">₹{pitch.additional_services_total.toLocaleString()}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-800 rounded">
                      {pitch.rooms_available} rooms available
                    </span>
                    {pitch.venue_available && (
                      <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                        Venue confirmed
                      </span>
                    )}
                    {pitch.dates_confirmed && (
                      <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-800 rounded">
                        Dates confirmed
                      </span>
                    )}
                    {pitch.is_within_deadline && (
                      <span className="text-xs px-2 py-1 bg-amber-100 text-amber-800 rounded">
                        On-time submission
                      </span>
                    )}
                  </div>

                  {pitch.complimentary_offers?.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-medium mb-1">Complimentary Offers:</p>
                      <div className="flex flex-wrap gap-1">
                        {pitch.complimentary_offers.map((offer, idx) => (
                          <span key={idx} className="text-xs px-2 py-1 bg-champagne-gold/20 text-amber-800 rounded">
                            {offer}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        pitch.status === 'selected' ? 'bg-emerald-100 text-emerald-800' :
                        pitch.status === 'shortlisted' ? 'bg-blue-100 text-blue-800' :
                        pitch.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-slate-100 text-slate-800'
                      }`}>
                        {pitch.status.toUpperCase()}
                      </span>
                      <span className="text-xs text-slate-800">Valid for {pitch.validity_days} days</span>
                    </div>
                    {pitch.status === 'submitted' && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleUpdatePitchStatus(pitch.id, 'shortlisted')}>
                          Shortlist
                        </Button>
                        <Button size="sm" className="bg-emerald-600" onClick={() => handleUpdatePitchStatus(pitch.id, 'selected')}>
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Select
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleUpdatePitchStatus(pitch.id, 'rejected')}>
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
